import { Ellipsis } from 'lucide-react'
import MobileBottomModal from 'components/mobile/MobileBottomModal'
import { FC, PropsWithChildren, useEffect, useState } from 'react'
import {
  cn,
  Button,
  ButtonProps,
  useIsMobile,
  DropdownMenu,
  TooltipWrapper,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from '@wellesley/ui'

import { Action } from './types'
import MenuContent from './MenuContent'
import useCheckIsInView from './useCheckIsInView'

export * from './types'

type ActionsDropdownProps = {
  align?: 'start' | 'end'
  actions: Action[]
  triggerClass?: string
  adminActions?: Action[]
  mobileBehavior?: 'modal' | 'dropdown'
  desktopModalBehavior?: boolean
  triggerSize?: ButtonProps['size']
  triggerVariant?: ButtonProps['variant']
  trigger?: React.ReactNode
  dropdownClassName?: string
  isPortal?: boolean
  onOpenChange?: (value: boolean) => void
  sideOffset?: number
  alignOffset?: number
  dataTestid?: string
}

const defaultTriggerClass =
  'flex size-m items-center justify-center border-gray-700 p-0 focus-visible:ring-0 focus-visible:ring-transparent'

const ActionsDropdown: FC<PropsWithChildren<ActionsDropdownProps>> = ({
  dataTestid,
  align = 'end',
  actions,
  children,
  adminActions = [],
  mobileBehavior = 'modal',
  isPortal = true,
  desktopModalBehavior = false,
  triggerVariant = 'none',
  triggerClass = defaultTriggerClass,
  trigger,
  dropdownClassName,
  onOpenChange,
  triggerSize = 'none',
  sideOffset,
  alignOffset,
}) => {
  const [isOpened, setIsOpened] = useState(false)

  const dialogs = [...actions, ...adminActions].map((a) =>
    a.renderDialog ? (
      <div className="empty:hidden" key={a.name}>
        {a.renderDialog?.()}
      </div>
    ) : null,
  )

  const isMobile = useIsMobile()

  const modalOnMobile = mobileBehavior === 'modal' && isMobile

  const handleOpenChange = (value: boolean) => {
    onOpenChange?.(value)
    setIsOpened(value)
  }

  const [isVisible, contentRef, triggerRef] = useCheckIsInView({
    canCheck: isOpened && !modalOnMobile,
    onOpenChange: handleOpenChange,
  })

  useEffect(() => {
    if (!isVisible) setIsOpened(false)
  }, [isVisible])

  return (
    <>
      {dialogs}
      {modalOnMobile ? (
        <MobileBottomModal
          childrenClassName="flex flex-col p-xxs gap-xxxs"
          renderToggler={(toggle) => (
            <Button
              onClick={toggle}
              size={triggerSize}
              variant={triggerVariant}
              className={cn(
                'size-m flex items-center justify-center border-gray-700 p-0 focus-visible:ring-0 focus-visible:ring-transparent',
                triggerClass,
              )}
            >
              <Ellipsis />
            </Button>
          )}
          renderContent={({ setOpen }) => (
            <MenuContent
              actions={actions}
              adminActions={adminActions}
              renderItemWrapper={(action, el) => (
                <div
                  onClick={() => {
                    if (action.disabled || action.rightElement) return

                    action.handler?.()
                    setOpen(false)
                  }}
                  className={cn(
                    'mb-xxs  p-xxs min-h-m flex cursor-pointer items-center text-sm font-semibold text-gray-700 last:mb-0',
                    action.className,
                    action.rightElement && 'cursor-default',
                  )}
                >
                  <TooltipWrapper
                    theme="dark"
                    tooltipText={action.tooltipText || ''}
                    size="small"
                    align="start"
                    triggerClassname="w-full"
                  >
                    {el}
                  </TooltipWrapper>
                </div>
              )}
            >
              {children}
            </MenuContent>
          )}
        />
      ) : (
        <DropdownMenu
          open={isOpened}
          modal={desktopModalBehavior}
          onOpenChange={handleOpenChange}
        >
          <DropdownMenuTrigger
            asChild
            ref={triggerRef}
            data-testid={dataTestid ?? 'actionsDropdown_menu_trigger'}
            className="focus:outline-none"
          >
            <Button
              size={triggerSize}
              variant={triggerVariant}
              className={cn(triggerClass)}
            >
              {trigger || (
                <Ellipsis
                  color={isOpened ? 'var(--blue-500)' : 'var(--gray-500)'}
                />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align={align}
            sideOffset={sideOffset}
            alignOffset={alignOffset}
            isPortal={isPortal}
            ref={contentRef}
            data-testid={
              dataTestid ? `${dataTestid}-content` : 'actionsDropdownMenu'
            }
            avoidCollisions={true}
            collisionPadding={8}
            sticky="always"
            className={cn(
              'p-xxs min-w-[200px] text-base font-semibold text-gray-700',
              dropdownClassName,
            )}
          >
            <MenuContent
              actions={actions}
              adminActions={adminActions}
              renderItemWrapper={(action, el) => (
                <TooltipWrapper
                  theme="dark"
                  tooltipText={action.tooltipText || ''}
                  size="small"
                  triggerClassname="w-full"
                >
                  <DropdownMenuItem
                    onClick={action.rightElement ? undefined : action.handler}
                    disabled={action.disabled}
                    className={cn(
                      'p-xxs text-base font-medium',
                      action.className,
                      action.rightElement ? 'cursor-default' : 'cursor-pointer',
                    )}
                    onSelect={(e) => {
                      if (action.rightElement) {
                        e.preventDefault()
                      }
                    }}
                  >
                    {el}
                  </DropdownMenuItem>
                </TooltipWrapper>
              )}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  )
}

export default ActionsDropdown
